import { Box, TextField, Button, FormHelperText } from "@mui/material";
import { FaMicrophone, FaMicrophoneSlash } from "react-icons/fa";
import Theme from "../../../../../utils/Theme";
import { useState, useRef, useEffect } from "react";
import { useVerification } from "../../../../../context/Verification.context";
import TextUnAvailable from "./TextUnAvailable";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";

// Ensure webkitSpeechRecognition is available for all browsers that use it
// (iOS Safari, macOS Safari, Chrome, Edge, etc.)
// This must be done before react-speech-recognition initializes
if (
  typeof window !== "undefined" &&
  !window.SpeechRecognition &&
  window.webkitSpeechRecognition
) {
  window.SpeechRecognition = window.webkitSpeechRecognition;
}

const SurveyMicComponent = (args) => {
  const { message, setMessage, isAISurvey } = args;

  const verification = useVerification();
  const [otherArtifact, setIsOtherArtifact] = useState({});
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [noVoiceMessage, setNoVoiceMessage] = useState("");
  const timeoutRef = useRef(null);
  const isListeningRef = useRef(false);
  const startCheckTimeoutRef = useRef(null);
  const expectingListeningRef = useRef(false);
  const mediaStreamRef = useRef(null); // Keep track of getUserMedia stream
  const androidStreamReleaseTimeoutRef = useRef(null);
  const CHAR_LIMIT = 150;
  const isSpanish = Boolean(verification?.aiData?.spanish);
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    finalTranscript,
  } = useSpeechRecognition();

  // Check if running on iOS or Android (mobile devices that may need special permission handling)
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  // Manual check for speech recognition support (iOS Safari uses webkitSpeechRecognition)
  const checkSpeechRecognitionSupport = () => {
    // Check manually for webkitSpeechRecognition (iOS Safari) or SpeechRecognition
    // This is important because react-speech-recognition might not detect webkitSpeechRecognition
    if (typeof window !== "undefined") {
      const SpeechRecognitionAPI =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognitionAPI) {
        // API exists, proceed even if library says it doesn't support it
        // The library should still work with the API
        return true;
      }
    }
    // Fall back to library's detection
    return browserSupportsSpeechRecognition;
  };

  const setPermissionError = (error) => {
    console.error("Microphone permission error:", error);
    setShowError(true);
    if (
      error.name === "NotAllowedError" ||
      error.name === "PermissionDeniedError"
    ) {
      if (isAndroid) {
        setErrorMessage(
          isSpanish
            ? "Permiso de micrófono denegado. Para permitir: toque el candado o 'i' en la barra de direcciones → Micrófono → Permitir. O en Ajustes de Chrome → Ajustes del sitio → Micrófono."
            : "Microphone denied. To allow: tap the lock or 'i' in the address bar → Microphone → Allow. Or Chrome → Settings → Site settings → Microphone.",
        );
      } else if (isIOS) {
        setErrorMessage(
          isSpanish
            ? "Permiso de micrófono denegado. Por favor, permita el acceso al micrófono en la configuración de Safari."
            : "Microphone permission denied. Please allow microphone access in Safari settings.",
        );
      } else {
        setErrorMessage(
          isSpanish
            ? "Permiso de micrófono denegado. Por favor, permita el acceso al micrófono en la configuración del navegador."
            : "Microphone permission denied. Please allow microphone access in browser settings.",
        );
      }
    } else if (error.name === "NotFoundError") {
      setErrorMessage(
        isSpanish ? "No se encontró ningún micrófono." : "No microphone found.",
      );
    } else {
      setErrorMessage(
        isSpanish
          ? "Error al acceder al micrófono. Por favor, inténtelo de nuevo."
          : "Error accessing microphone. Please try again.",
      );
    }
  };

  const handleMicClick = () => {
    if (isListeningRef.current) {
      stopRecording();
      return;
    }

    const hasSupport = checkSpeechRecognitionSupport();
    if (!hasSupport) {
      setShowError(true);
      setErrorMessage(
        isSpanish
          ? "El navegador no admite reconocimiento de voz."
          : "Browser doesn't support speech recognition.",
      );
      return;
    }

    if (
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function"
    ) {
      // On Android Chrome the permission prompt only shows when getUserMedia is
      // invoked in the same synchronous call stack as the user tap. Do not await
      // or use async – use .then()/.catch() so the handler returns immediately.
      const promise = navigator.mediaDevices.getUserMedia({ audio: true });
      promise
        .then((stream) => {
          if (isAndroid) {
            stream.getTracks().forEach((t) => t.stop());
            mediaStreamRef.current = null;
          } else {
            mediaStreamRef.current = stream;
          }
          resetTranscript();
          startRecording();
        })
        .catch(setPermissionError);
      return;
    }

    resetTranscript();
    startRecording();
  };

  const startRecording = () => {
    try {
      setMessage("");
      setShowError(false);
      setErrorMessage("");
      setNoVoiceMessage(""); // Clear no voice message when starting new recording

      // Clear any existing start check timeout
      if (startCheckTimeoutRef.current) {
        clearTimeout(startCheckTimeoutRef.current);
      }

      expectingListeningRef.current = true;
      SpeechRecognition.startListening({
        continuous: true,
        language: isSpanish ? "es-ES" : "en-US",
        interimResults: true,
      });
      isListeningRef.current = true;
      resetInactivityTimeout();

      // Check if recording actually started after a short delay
      // This helps catch iOS permission issues
      // Note: If listening becomes true, the useEffect will clear expectingListeningRef
      // and cancel this timeout, so we only check expectingListeningRef here
      startCheckTimeoutRef.current = setTimeout(() => {
        if (expectingListeningRef.current) {
          console.warn(
            "Speech recognition did not start - possible permission issue",
          );
          setShowError(true);
          const browserName = isIOS
            ? "Safari"
            : isAndroid
              ? "your browser"
              : "your browser";
          setErrorMessage(
            isSpanish
              ? `No se pudo iniciar el reconocimiento de voz. Verifique los permisos del micrófono en la configuración de ${browserName}.`
              : `Could not start speech recognition. Please check microphone permissions in ${browserName} settings.`,
          );
          isListeningRef.current = false;
          expectingListeningRef.current = false;
          try {
            SpeechRecognition.stopListening();
          } catch (e) {
            // Ignore errors when stopping
          }
        }
        startCheckTimeoutRef.current = null;
      }, 1000);
    } catch (error) {
      console.error("Error starting speech recognition:", error);
      setShowError(true);
      setErrorMessage(
        isSpanish
          ? "Error al iniciar el reconocimiento de voz. Por favor, inténtelo de nuevo."
          : "Error starting speech recognition. Please try again.",
      );
      isListeningRef.current = false;
      expectingListeningRef.current = false;
    }
  };

  const stopRecording = () => {
    SpeechRecognition.stopListening();
    isListeningRef.current = false;
    expectingListeningRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (startCheckTimeoutRef.current) {
      clearTimeout(startCheckTimeoutRef.current);
      startCheckTimeoutRef.current = null;
    }
    if (androidStreamReleaseTimeoutRef.current) {
      clearTimeout(androidStreamReleaseTimeoutRef.current);
      androidStreamReleaseTimeoutRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  };

  useEffect(() => {
    isListeningRef.current = listening;
    if (!listening && androidStreamReleaseTimeoutRef.current) {
      clearTimeout(androidStreamReleaseTimeoutRef.current);
      androidStreamReleaseTimeoutRef.current = null;
    }
    if (listening && expectingListeningRef.current) {
      expectingListeningRef.current = false;
      if (startCheckTimeoutRef.current) {
        clearTimeout(startCheckTimeoutRef.current);
        startCheckTimeoutRef.current = null;
      }
      // Option 3: On Android we never hold the stream (released right after permission).
      // On iOS we keep the getUserMedia stream for the whole session so recognition works.
      // On desktop we release our stream once recognition is listening.
      if (!isIOS && !isAndroid && mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
    }
  }, [listening]);

  useEffect(() => {
    if (transcript) {
      setMessage(transcript);
      setShowError(false);
      setErrorMessage("");
      setNoVoiceMessage(""); // Clear no voice message when voice is detected

      resetInactivityTimeout();
    }
  }, [transcript]);

  useEffect(() => {
    if (finalTranscript) {
      stopRecording();
    }
  }, [finalTranscript]);

  useEffect(() => {
    if (verification?.tradeQuote?.length > 0) {
      let findOther = verification?.tradeQuote?.find(
        (item) => item?.typeName === "surveyOther",
      );
      if (findOther) setIsOtherArtifact(findOther);
    }
  }, []);

  // Cleanup on unmount: stop listening and release mic so it's disabled when user navigates away
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (startCheckTimeoutRef.current)
        clearTimeout(startCheckTimeoutRef.current);
      if (androidStreamReleaseTimeoutRef.current) {
        clearTimeout(androidStreamReleaseTimeoutRef.current);
        androidStreamReleaseTimeoutRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      try {
        SpeechRecognition.stopListening();
      } catch (e) {
        // ignore if already stopped
      }
      isListeningRef.current = false;
      expectingListeningRef.current = false;
    };
  }, []);

  // Updated: Button color based on state
  const getButtonColor = () => {
    if (showError) return Theme.palette.primary.main;
    return listening ? Theme.palette.error.main : Theme.palette.primary.main;
  };

  // Updated: Button text based on state
  const getButtonText = () => {
    if (listening) {
      return isSpanish ? "Detener" : "Stop";
    }
    return isSpanish ? "Iniciar" : "Start";
  };

  // Updated: Button icon based on state
  const getButtonIcon = () => {
    if (listening) {
      return <FaMicrophoneSlash size={20} />;
    }
    return <FaMicrophone size={20} />;
  };

  const errorMessageHandler = () => {
    if (errorMessage) {
      return errorMessage;
    }
    if (!browserSupportsSpeechRecognition) {
      return isSpanish
        ? "El navegador no admite reconocimiento de voz."
        : "Browser doesn't support speech recognition.";
    }
    return isSpanish
      ? "No se detectó el audio. Inténtelo de nuevo."
      : "Speech not detected. Please try again.";
  };

  const resetInactivityTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      // If we've been listening but transcript hasn't changed for 5s, stop.
      if (isListeningRef.current) {
        console.log("Stopping due to 5s silence");
        // Show message when no voice is detected (not an error)
        setNoVoiceMessage(
          isSpanish
            ? "No se detectó voz. Por favor, intente hablar de nuevo."
            : "No voice detected. Please try speaking again.",
        );
        stopRecording();
      }
    }, 5000);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "row",
          gap: 2,
        }}
      >
        {!isAISurvey && (
          <TextUnAvailable
            text={otherArtifact?.text}
            es_text={otherArtifact?.es_text}
          />
        )}
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            gap: 2,
            p: 1,
            alignItems: "stretch",
          }}
        >
          <TextField
            placeholder={
              isSpanish ? "Escriba su respuesta" : "Type your answer"
            }
            multiline
            fullWidth
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setShowError(false);
              setErrorMessage("");
              setNoVoiceMessage("");
            }}
            sx={{
              flex: 1,
              bgcolor: isAISurvey ? "white" : null,
              borderRadius: 2,
              border: showError
                ? `1px solid ${Theme.palette.error.main}`
                : "1px solid #009349",

              "& .MuiOutlinedInput-root": {
                minHeight: "56px",
                maxHeight: "120px",
                alignItems: "flex-start",
              },
              "& .MuiOutlinedInput-input": {
                overflow: "auto !important",
                maxHeight: "96px",
              },

              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
            inputProps={{ maxLength: CHAR_LIMIT }}
            error={showError}
            helperText={listening ? "Listening..." : ""}
            FormHelperTextProps={{ sx: { fontWeight: "bold" } }}
          />

          <Button
            variant="contained"
            onClick={handleMicClick}
            startIcon={getButtonIcon()}
            sx={{
              width: { xs: "120px", sm: "150px" }, // 👈 small but responsive
              height: "56px",
              flexShrink: 0, // 👈 never shrink
              bgcolor: getButtonColor(),
              color: "white",
              whiteSpace: "nowrap",

              "&:hover": {
                bgcolor: listening
                  ? Theme.palette.error.dark
                  : Theme.palette.primary.dark,
              },
            }}
          >
            {getButtonText()}
          </Button>
        </Box>
        {showError && (
          <Box
            sx={{
              width: "100%",
              mt: 0.5,
              p: 1.5,
              bgcolor: "white",
            }}
          >
            <FormHelperText
              error
              sx={{
                m: 0,
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              {errorMessageHandler()}
            </FormHelperText>
          </Box>
        )}
        {noVoiceMessage && !showError && (
          <Box
            sx={{
              width: "100%",
              mt: 0.5,
              p: 1.5,
              bgcolor: "white",
            }}
          >
            <FormHelperText
              sx={{
                m: 0,
                fontSize: "0.875rem",
                fontWeight: 500,
                color: Theme.palette.warning?.main || "#ed6c02",
              }}
            >
              {noVoiceMessage}
            </FormHelperText>
          </Box>
        )}
      </Box>
    </>
  );
};

export default SurveyMicComponent;
